/* global Vue, calendar */

import { useState } from './Notifications'
import { MAX_NIGHTS } from '../../enums/limits'
import { compareDates, denormalizeTimezone, normalizeTimezone } from '../../utils-common'

export default {
  name: 'BookingCalendar',
  components: { Calendar: calendar },
  template: `
    <div class="booking-calendar">
      <Calendar
        v-if="!state.isLoading.value && showCalendar"
        v-model="dateRange"
        selectionMode="range"
        :minDate="denormalizeTimezone(getYesterday())"
        :maxDate="maxDate"
        :numberOfMonths="2"
        :disabledDates="disabledDates"
        :inline="true"
        :selectOtherMonths="true"
        :showButtonBar="false"
        @date-select="onDateSelect">
        <template #date="slotProps">
          <span class="booking-calendar__tooltip" v-if="!slotProps.date.selectable">{{ $t('bookingModal.unavailable') }}</span>
          <span class="booking-calendar__tooltip" v-if="isCheckoutOnly(slotProps.date)">{{ $t('bookingModal.checkoutOnly') }}</span>
          <span class="booking-calendar__day"
            :class="calendarDayClassList(slotProps.date)"
            @mouseover="hoverHighlight(slotProps)"
            >
            <span class="day">{{slotProps.date.day}}</span>
          </span>
        </template>
      </Calendar>

      <div v-if="specialOffer?.value?.title" class="booking-calendar__disclaimer">
        <span>{{ $t('bookingModal.specialOffer') }}</span>
      </div>

      <Teleport to="#modal-footer-teleport" v-if="!noButton">
        <div class="modal__buttons">
          <button
            class="button no-margin-top"
            @click="dateSelected">
            {{ $t('bookingModal.selectBtn') }}
          </button>
        </div>
      </Teleport>
    </div>
  `,

  props: {
    startDate: Date,
    nights: Number,
    availabilityCalendar: Object,
    specialOffer: Object,
    forCheckRates: Boolean,
    noButton: Boolean,
  },
  emits: ['dateSelected'],

  setup (props, { emit }) {
    const { state } = useState()
    const { startDate, nights, forCheckRates } = Vue.toRefs(props)
    const availabilityCalendar = props.availabilityCalendar
    const dateRange = Vue.ref([])
    const hoveredEndDate = Vue.ref('')
    const showCalendar = Vue.ref(false)

    const disabledDates = Vue.computed(() => {
      // adding yesterday as disabled date instead of adding today as minDate because the calendar also uses current time which messes up the timezone normalization
      if (availabilityCalendar.hasAvailableDates) {
        return [
          ...availabilityCalendar.disabledDates.map(d => denormalizeTimezone(d)),
          denormalizeTimezone(getYesterday()),
          ...(dateRange.value[0] && !dateRange.value[1] ? checkOutOnlyDates.value.filter(d => d.getTime() < dateRange.value[0].getTime()) : checkOutOnlyDates.value),
        ]
      }
      return [denormalizeTimezone(getYesterday())]
    })
    const checkOutOnlyDates = Vue.computed(() => {
      if (availabilityCalendar.hasAvailableDates) {
        return [...availabilityCalendar.checkoutOnlyDates]
      }
      return []
    })
    const maxDate = Vue.computed(() => {
      if (availabilityCalendar.hasAvailableDates) {
        return maxAvailableDate.value
      }
      return null
    })
    const maxAvailableDate = Vue.computed(() => {
      if (availabilityCalendar.hasAvailableDates) {
        return availabilityCalendar.maxAvailableDate.date
      }
      return null
    })

    function dateSelected () {
      if (!dateRange.value[0]) {
        dateRange.value[0] = new Date()
      }

      emit('dateSelected', {
        startDate: normalizeTimezone(dateRange.value[0]),
        nights: Math.ceil((Date.parse(normalizeTimezone(dateRange.value[1]).toISOString().substring(0, 10)) - Date.parse(normalizeTimezone(dateRange.value[0]).toISOString().substring(0, 10))) / (1000 * 3600 * 24)),
      })
    }

    function onDateSelect (selectedDate) {
      if (checkOutOnlyDates.value.find(d => compareDates(denormalizeTimezone(d), dateRange.value[0]))) {
        dateRange.value = []
        return
      }

      const allNonAllowedDates = [...checkOutOnlyDates.value, ...disabledDates.value]
      if (allNonAllowedDates.length) {
        if (dateRange.value[1]) {
          for (const date of allNonAllowedDates) {
            if (denormalizeTimezone(date.getTime()) > dateRange.value[0].getTime() && denormalizeTimezone(date).getTime() < dateRange.value[1].getTime()) {
              delete dateRange.value[1]
              dateRange.value[0] = normalizeTimezone(selectedDate)
              return
            }
          }
          if (disabledDates.value.find(d => compareDates(d, dateRange.value[1])) && !(checkOutOnlyDates.value.find(d => compareDates(d, dateRange.value[1])))) {
            delete dateRange.value[1]
            return
          }
        }
      }
      if (!maxAvailableDate.value) {
        maxDate.value = null
      } else if (dateRange.value[1] != null) {
        maxDate.value = maxAvailableDate.value
      } else {
        const date = new Date(dateRange.value[0])
        date.setDate(date.getDate() + MAX_NIGHTS)
        maxDate.value = date.getTime() < maxAvailableDate.value.getTime() ? date : maxAvailableDate.value
      }

      Vue.nextTick(() => {
        const selectedDays = document.querySelectorAll('td:not(.p-datepicker-other-month) .p-highlight')
        selectedDays[0]?.classList.add('first-selected')
        if (selectedDays.length >= 2) selectedDays[selectedDays.length - 1]?.classList.add('last-selected')
      }, 0)

      if (dateRange.value[0] && dateRange.value[1] && compareDates(dateRange.value[0], dateRange.value[1])) {
        const tomorrow = new Date(dateRange.value[0])
        tomorrow.setDate(tomorrow.getDate() + 1)
        dateRange.value[1] = tomorrow
      }
      if (props.noButton && dateRange.value[0] && dateRange.value[1]) {
        dateSelected()
        return
      }

      if (!forCheckRates.value && window.matchMedia('(min-width: 768px)').matches && dateRange.value[0] && dateRange.value[1]) {
        dateSelected()
      }
    }

    function getCurrentDay (date) {
      const currentDate = new Date(date.year, date.month, date.day).getTime()
      return availabilityCalendar.availability.find(obj => denormalizeTimezone(obj.date).getTime() === currentDate)
    }

    function isCheckoutOnly (date) {
      const day = getCurrentDay(date)
      return !!(day && checkOutOnlyDates.value.find(d => compareDates(d, day.date)))
    }

    function getYesterday () {
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return yesterday
    }

    function getEndDate (start, nights) {
      const endDate = new Date(start)
      return new Date(endDate.setDate(endDate.getDate() + nights))
    }

    function selectFirstAvailableDate () {
      dateRange.value = []
      const firstAvailableDate = availabilityCalendar.minAvailableDate
      if (firstAvailableDate != null) {
        dateRange.value[0] = denormalizeTimezone(firstAvailableDate.date)
        dateRange.value[1] = denormalizeTimezone(getEndDate(firstAvailableDate.date, nights.value))
      }
    }

    function hoverHighlight ({ date: { year, month, day } }) {
      const hoveredDate = normalizeTimezone(new Date(year, month, day)).toISOString().substring(0, 10)
      const [start] = dateRange.value.map(d => d?.toISOString().substring(0, 10))
      const lastPossibleHoverDate = checkOutOnlyDates.value.find(d => d.toISOString().substring(0, 10) > start && d.toISOString().substring(0, 10) < hoveredDate)
      hoveredEndDate.value = lastPossibleHoverDate ? lastPossibleHoverDate.toISOString().substring(0, 10) : hoveredDate
    }

    function isInHoveredRange (date) {
      if (!hoveredEndDate.value) return false

      const [start, end] = dateRange.value.map(d => d ? normalizeTimezone(d).toISOString().substring(0, 10) : null)
      if (end) return false

      return date >= start && hoveredEndDate.value > start && date <= hoveredEndDate.value
    }

    function calendarDayClassList ({ year, month, day, selectable }) {
      const normalizedDate = normalizeTimezone(new Date(year, month, day)).toISOString().substring(0, 10)
      const hoveredRange = isInHoveredRange(normalizedDate)

      return {
        disabled: !selectable,
        checkoutOnly: isCheckoutOnly({ year, month, day }) && !hoveredRange,
        hoveredRange: hoveredRange,
        lastHovered: dateRange.value[0] && hoveredRange && normalizedDate === hoveredEndDate.value,
        startDate: dateRange.value[0] && normalizedDate === normalizeTimezone(dateRange.value[0]).toISOString().substring(0, 10),
        endDate: dateRange.value[1] && normalizedDate === normalizeTimezone(dateRange.value[1]).toISOString().substring(0, 10),
      }
    }

    dateRange.value[0] = denormalizeTimezone(startDate.value)
    dateRange.value[1] = denormalizeTimezone(getEndDate(startDate.value, nights.value))

    Vue.watch(() => availabilityCalendar.availability, () => {
      if (availabilityCalendar.hasAvailableDates && !availabilityCalendar.isDateAvailable(normalizeTimezone(new Date(dateRange.value[0])))) {
        selectFirstAvailableDate()
        dateSelected()
      }
    }, { immediate: true })

    Vue.onMounted(() => {
      // Delay display of calendar on touch devices to prevent incorrect hover state
      if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
        setTimeout(() => {
          showCalendar.value = true
        }, 100)
      } else {
        showCalendar.value = true
      }
    })

    return {
      state,
      dateRange,
      disabledDates,
      maxDate,
      onDateSelect,
      dateSelected,
      isCheckoutOnly,
      denormalizeTimezone,
      getYesterday,
      hoverHighlight,
      calendarDayClassList,
      showCalendar,
    }
  },
}
